import React from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IconCrossThick } from '@sololearnorg/sol';
import { Container, i18n } from '../../../../../symphony';

import { SlLEContext } from '../../../global-constants';
import {
	ActionType,
	IMaterialProps,
	RequestType,
} from '../../../global-interface';
import { SlActions } from '../../../services/ActionsService/sl-actions';
import { SlDataService } from '../../../services/sl-data.service';
import { useContainerData } from '../../../../../shared/public/SlHooks/useContainerData';
import { ProgrammingLanguageToCodeSnippetLanguage } from '../../../../../shared/public/constants/constants';
import { SlCodeSnippet } from '../../../../../shared/public/SlLearnEngine/SlCodeSnippet/SlCodeSnippet';

import s from './ExternalResource.scss';
import { CodeSnippetLanguage } from '../../../../../api/private/learn-engine-api/learn-engine-api.interface';

const languagesMapping = {
	[CodeSnippetLanguage.Python]: 'PY',
	[CodeSnippetLanguage.HTML]: 'HTML',
	[CodeSnippetLanguage.CSS]: 'CSS',
	[CodeSnippetLanguage.JS]: 'JS',
	[CodeSnippetLanguage.NodeJS]: 'NODE',
	[CodeSnippetLanguage.Java]: 'JAVA',
	[CodeSnippetLanguage.Kotlin]: 'KT',
	[CodeSnippetLanguage.CPP]: 'C++',
	[CodeSnippetLanguage.CSHARP]: 'C#',
	[CodeSnippetLanguage.C]: 'C',
	[CodeSnippetLanguage.Go]: 'GO',
	[CodeSnippetLanguage.R]: 'R',
	[CodeSnippetLanguage.SQL]: 'SQL',
	[CodeSnippetLanguage.Ruby]: 'RB',
	[CodeSnippetLanguage.Swift]: 'SWIFT',
	[CodeSnippetLanguage.PHP]: 'PHP',
	[CodeSnippetLanguage.WEB]: 'WEB',
	[CodeSnippetLanguage.TS]: 'TS',
};

export function ExternalResource({
	materialInfo: {
		header: {
			description,
			uiConfigurations: { languageId, resourceUrl },
			title,
			typeId,
			materialRelationId,
		},
	},
}: IMaterialProps): JSX.Element {
	useStyles(s);
	const { actions$ } = Container.take(SlLEContext, SlActions);
	const { externalResourceUrl$: externalResourceUrl } = useContainerData(
		SlLEContext,
		SlDataService,
		['externalResourceUrl$'],
	);

	const onRunClickHandler = (): void => {
		actions$.next({
			type: ActionType.OnRunExternalResourceClick,
			requestType: RequestType.Parallel,
			data: {
				externalResourceUrl: resourceUrl,
				typeId,
				materialRelationId,
			},
		});
	};

	const closeHandler = () => {
		actions$.next({
			type: ActionType.OnRunExternalResourceCloseClick,
			requestType: RequestType.Parallel,
		});
	};

	return (
		<div className="le-external-resource">
			{externalResourceUrl && (
				<div className="le-external-resource__playground">
					<div className="le-external-resource__playground__header">
						<IconCrossThick
							className="le-external-resource__playground__header__close-icon"
							onClick={closeHandler}
						/>
						<p>{title}</p>
					</div>
					<iframe src={resourceUrl} title={title} />
				</div>
			)}
			<div className="le-external-resource__body">
				<div className="le-external-resource__header">
					<span>CODE PLAYGROUND</span>
					<span>
						{
							languagesMapping[
								ProgrammingLanguageToCodeSnippetLanguage[
									languageId
								]
							]
						}
						{' '}
					</span>
				</div>
				<SlCodeSnippet content={description} hideCopyButton />
			</div>
			<button
				type="button"
				onClick={onRunClickHandler}
				className="le-external-resource__run-btn"
			>
				{i18n.t('learn.try-it-yourself.click-to-run')}
			</button>
		</div>
	);
}
